import { useEffect } from 'react'

export interface KeyboardShortcut {
  key: string
  ctrlKey?: boolean
  metaKey?: boolean
  shiftKey?: boolean
  altKey?: boolean
  handler: () => void
  description: string
  preventDefault?: boolean
}

interface UseKeyboardShortcutsOptions {
  shortcuts: KeyboardShortcut[]
  enabled?: boolean
}

/**
 * Hook to register keyboard shortcuts with support for modifier keys
 * Automatically handles Ctrl (Windows/Linux) vs Cmd (Mac)
 */
export function useKeyboardShortcuts({
  shortcuts,
  enabled = true,
}: UseKeyboardShortcutsOptions) {
  useEffect(() => {
    if (!enabled) return

    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in inputs, textareas, or contenteditable
      const target = event.target as HTMLElement
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return
      }

      // Find matching shortcut
      const matchingShortcut = shortcuts.find((shortcut) => {
        const keyMatches =
          event.key.toLowerCase() === shortcut.key.toLowerCase()

        // Handle Ctrl/Cmd (use metaKey on Mac, ctrlKey on Windows/Linux)
        const modifierMatches =
          shortcut.ctrlKey || shortcut.metaKey
            ? event.metaKey || event.ctrlKey
            : true

        const shiftMatches = shortcut.shiftKey
          ? event.shiftKey
          : !event.shiftKey
        const altMatches = shortcut.altKey ? event.altKey : !event.altKey

        return keyMatches && modifierMatches && shiftMatches && altMatches
      })

      if (matchingShortcut) {
        if (matchingShortcut.preventDefault !== false) {
          event.preventDefault()
        }
        matchingShortcut.handler()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [shortcuts, enabled])
}

/**
 * Get display text for keyboard shortcut (handles Mac vs Windows/Linux)
 */
export function getShortcutDisplay(shortcut: KeyboardShortcut): string {
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
  const parts: string[] = []

  if (shortcut.ctrlKey || shortcut.metaKey) {
    parts.push(isMac ? '⌘' : 'Ctrl')
  }
  if (shortcut.shiftKey) {
    parts.push(isMac ? '⇧' : 'Shift')
  }
  if (shortcut.altKey) {
    parts.push(isMac ? '⌥' : 'Alt')
  }

  // Format key display
  let keyDisplay = shortcut.key
  if (keyDisplay === 'ArrowRight') keyDisplay = '→'
  if (keyDisplay === 'ArrowLeft') keyDisplay = '←'
  if (keyDisplay === 'Enter') keyDisplay = '↵'
  if (keyDisplay === 'Escape') keyDisplay = 'Esc'

  parts.push(keyDisplay.toUpperCase())

  return parts.join(isMac ? '' : '+')
}
